import { useEffect, useMemo } from 'react'
import { GitBranch, MessageSquarePlus, Zap } from 'lucide-react'
import { toast } from 'sonner'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { CloudDispatchBanner } from '@/components/features/agent/cloud-dispatch-banner'
import { useCloudDispatchForDocument } from '@/hooks/use-cloud-dispatch-for-document'
import { isActiveCloudDispatch } from '@/lib/cloud-dispatch'
import type { RoadmapItem } from './product-board-types'
import type { CloudDispatchResultEntry } from './product-board.utils'
import {
  isDispatchBlocked,
  isDispatchReady,
  isShapingNeeded,
  normalizeBetDocFilePath,
} from './product-board.utils'
import { resolveCloudDispatchTargets } from './bet-handoff-path'

export type BetPreviewCloudDispatchSectionProps = {
  item: RoadmapItem
  organizationId?: string | null
  onWatchLive: (agentId: string) => void
  /** Keeps roadmap card dispatch badge in sync with DB-backed preview state */
  onDispatchSync?: (itemId: string, entry: CloudDispatchResultEntry | null) => void
  /** Skip dispatch-for-document unless we know a run exists (avoids N+1 on every preview open) */
  fetchEnabled?: boolean
  /** Session state from product board (survives until reload; not wiped when DB fetch is empty) */
  sessionDispatch?: CloudDispatchResultEntry | null
  /** Loaded bet markdown (parse **Handoff:** when handoffPath not yet enriched) */
  betPreviewMarkdown?: string | null
}

export function BetPreviewCloudDispatchSection({
  item,
  organizationId,
  onWatchLive,
  onDispatchSync,
  fetchEnabled = false,
  sessionDispatch,
  betPreviewMarkdown,
}: BetPreviewCloudDispatchSectionProps) {
  const targets = resolveCloudDispatchTargets(item, betPreviewMarkdown)
  const { dispatchPath, dispatchDocumentId, betPath, handoffPath } = targets
  const { dispatch, isDispatching, isActiveDispatch, dispatchToCloud } = useCloudDispatchForDocument({
    documentId: dispatchDocumentId,
    filePath: dispatchPath,
    fallbackFilePath: handoffPath && betPath && handoffPath !== betPath ? betPath : null,
    organizationId,
    enabled: fetchEnabled && Boolean(dispatchPath || dispatchDocumentId?.trim()),
  })

  const effectiveDispatch = useMemo(() => {
    if (dispatch?.agentId) return dispatch
    if (sessionDispatch?.agentId) {
      return {
        agentId: sessionDispatch.agentId,
        title: sessionDispatch.title || item.title,
        dispatchedAt: sessionDispatch.dispatchedAt,
        prUrl: sessionDispatch.prUrl ?? null,
        threadId: sessionDispatch.threadId ?? null,
        dispatchStatus: sessionDispatch.dispatchStatus,
        isActive: sessionDispatch.isActive,
        error: sessionDispatch.error,
      }
    }
    const stampedAgentId = item.dispatchedAgentId?.trim()
    if (stampedAgentId) {
      return {
        agentId: stampedAgentId,
        title: item.title,
        dispatchedAt: '',
        dispatchStatus: item.dispatchStatus ?? 'running',
        isActive: item.dispatchStatus ? item.dispatchStatus === 'running' : true,
      }
    }
    return null
  }, [dispatch, sessionDispatch, item.title, item.dispatchedAgentId, item.dispatchStatus])

  const effectiveActive = isActiveCloudDispatch(effectiveDispatch)

  useEffect(() => {
    if (!onDispatchSync || !dispatch?.agentId) return
    onDispatchSync(item.id, {
      agentId: dispatch.agentId,
      title: dispatch.title || item.title,
      dispatchedAt: dispatch.dispatchedAt,
      prUrl: dispatch.prUrl ?? null,
      threadId: dispatch.threadId ?? null,
      dispatchStatus: dispatch.dispatchStatus,
      isActive: dispatch.isActive,
    })
  }, [dispatch, item.id, item.title, onDispatchSync])

  const handleDispatch = async () => {
    const result = await dispatchToCloud('flownotes')
    if (result.success) {
      toast.success('Cloud agent dispatched', {
        description: `Agent ${result.agentId} is implementing "${result.title || item.title}"`,
      })
    } else if (result.error) {
      toast.error('Dispatch failed', { description: result.error })
    }
  }

  const showDispatchRow =
    !effectiveActive && !isActiveDispatch && !isDispatching && Boolean(dispatchPath || betPath)

  return (
    <>
      <CloudDispatchBanner
        dispatch={effectiveDispatch}
        isDispatching={isDispatching}
        workspaceId={organizationId}
        onWatchLive={onWatchLive}
      />

      {showDispatchRow ? (
        <div className="px-4 py-2 border-b border-border flex justify-end gap-3">
          {item.gitMrUrl ? (
            <a
              href={item.gitMrUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs text-purple-400 hover:text-purple-300 transition-colors font-medium"
            >
              <GitBranch className="w-3.5 h-3.5" />
              View MR
            </a>
          ) : null}
          {isShapingNeeded(item) ? (
            <button
              type="button"
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-blue-400 transition-colors"
              onClick={() => {
                const docPath = normalizeBetDocFilePath(item.path)!
                window.dispatchEvent(
                  new CustomEvent('add-to-chat', {
                    detail: {
                      type: 'document',
                      id: item.documentId || item.id,
                      title: item.title,
                      name: item.title,
                      documentType: 'bet',
                      path: docPath,
                    },
                  })
                )
                toast.success('Bet added to chat', {
                  description: 'Ask me to help shape it into an implementation-ready handoff',
                })
              }}
            >
              <MessageSquarePlus className="w-3.5 h-3.5" />
              Shape in chat
            </button>
          ) : isDispatchReady(item) ? (
            <button
              type="button"
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-blue-400 transition-colors"
              onClick={() => void handleDispatch()}
            >
              <Zap className="w-3.5 h-3.5" />
              {item.gitMrUrl ? 'Re-dispatch' : 'Dispatch to Cloud Agent'}
            </button>
          ) : isDispatchBlocked(item) ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="flex items-center gap-1.5 text-xs text-muted-foreground opacity-50 cursor-not-allowed select-none">
                    <Zap className="w-3.5 h-3.5" />
                    Dispatch to Cloud Agent
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p className="text-xs">
                    Set <code>safe_to_dispatch: true</code> in the bet doc to enable
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : null}
        </div>
      ) : null}
    </>
  )
}
